import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, UserPlus, RefreshCcw, FileClock, Users2, ArrowRight, Sparkles } from 'lucide-react';
import { api } from '../lib/api';
import { fmtDate, fmtTime } from '../lib/format';

/**
 * Phase 3 widget surfaced on the Tax Specialist's home dashboard.
 *
 * Renders four headline counters + the five most recent direct bookings created
 * this week. Mounted by AdminDashboard when the signed-in user is a TS.
 */
function StatTile({ icon: Icon, label, value, tint, to }) {
  const inner = (
    <div className="rounded-xl border border-neutral-100 bg-white p-5 hover:border-[#f5b300] transition group">
      <div className="flex items-center justify-between">
        <div className={`w-10 h-10 rounded-md ${tint} flex items-center justify-center`}>
          <Icon className="w-4 h-4" strokeWidth={2} />
        </div>
        {to && <ArrowRight className="w-3.5 h-3.5 text-neutral-300 group-hover:text-[#c89200]" />}
      </div>
      <p className="mt-3 text-[28px] font-extrabold text-[#14110f] leading-none">{value}</p>
      <p className="text-[12px] text-neutral-500 mt-1.5">{label}</p>
    </div>
  );
  return to
    ? <Link data-testid={`spec-tile-${label.toLowerCase().replace(/\s+/g, '-')}`} to={to}>{inner}</Link>
    : <div data-testid={`spec-tile-${label.toLowerCase().replace(/\s+/g, '-')}`}>{inner}</div>;
}

export default function SpecialistDashboardWidget() {
  const [data, setData] = useState(null);
  const [slug, setSlug] = useState(null);

  useEffect(() => {
    api.specialistDashboardStats().then(setData).catch(() => setData(null));
    api.specialistMySlug().then((r) => setSlug(r.slug)).catch(() => setSlug(null));
  }, []);

  if (!data) return null;
  return (
    <section data-testid="specialist-dashboard-widget"
             className="rounded-xl bg-gradient-to-br from-[#fff8e3] to-white border border-[#f5b300]/30 p-5 md:p-6 mb-8">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-wider text-[#c89200] font-bold">Tax Specialist · This week</p>
          <h2 className="text-[18px] font-extrabold text-[#14110f] flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#c89200]" />Your direct bookings + change requests
          </h2>
        </div>
        {slug && (
          <Link data-testid="view-public-profile-link"
                to={`/specialists/${slug}`}
                className="text-[12.5px] text-[#c89200] font-semibold hover:underline">
            View public profile →
          </Link>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatTile icon={Calendar} label="New direct bookings"
                  value={data.new_direct_bookings_this_week}
                  tint="bg-[#f5b300]/15 text-[#c89200]" />
        <StatTile icon={UserPlus} label="Incoming requests"
                  value={data.pending_change_requests_incoming}
                  tint="bg-emerald-50 text-emerald-600" />
        <StatTile icon={RefreshCcw} label="Outgoing requests"
                  value={data.pending_change_requests_outgoing}
                  tint="bg-blue-50 text-blue-600" />
        <StatTile icon={Users2} label="Assigned members"
                  value={data.assigned_members}
                  tint="bg-neutral-100 text-neutral-700"
                  to="/admin/clients" />
      </div>

      {data.recent_bookings && data.recent_bookings.length > 0 && (
        <div className="mt-5 border-t border-[#f5b300]/30 pt-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] uppercase tracking-wider text-neutral-500 font-bold flex items-center gap-1.5">
              <FileClock className="w-3.5 h-3.5" />Most recent bookings
            </p>
            <Link to="/admin/appointments"
                  className="text-[11.5px] text-[#c89200] font-semibold hover:underline">
              View all →
            </Link>
          </div>
          <ul data-testid="recent-bookings-list" className="space-y-1.5">
            {data.recent_bookings.map((b) => (
              <li key={b.id} className="flex items-center gap-2 text-[12.5px] text-neutral-700">
                <Calendar className="w-3.5 h-3.5 text-[#c89200]" />
                <span className="font-semibold truncate max-w-[180px]">{b.client_name || '—'}</span>
                <span className="text-neutral-400">·</span>
                <span className="truncate">{b.appointment_type_name}</span>
                <span className="text-neutral-400">·</span>
                <span className="text-neutral-500">{fmtDate(b.start)} {fmtTime(b.start)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
